import React, { useEffect, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, Radio, Calendar, Users, Bell,
  Settings, ChevronLeft, ChevronRight, Zap, Volume2, Shield, TableProperties,
  Camera, Wallet, Activity, BarChart3, X, LogOut, FlaskConical, GitCompare, Sparkles,
} from 'lucide-react';
import t from '../i18n/it';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'TT Dashboard', exact: true },
  { path: '/tt/top-picks', icon: Zap, label: 'Top Picks' },
  { path: '/tt/matches', icon: TableProperties, label: 'TT Match' },
  { path: '/bet-tracking', icon: Wallet, label: 'Bet Tracking' },
  { path: '/admin/model-compare', icon: GitCompare, label: 'MW Compare' },
  { path: '/admin/performance', icon: BarChart3, label: 'Performance' },
  { path: '/pattern-lab', icon: FlaskConical, label: 'Pattern Lab' },
  { path: '/ai-advisor', icon: Sparkles, label: 'AI Advisor' },
  { path: '/confronta-quote', icon: Camera, label: 'Confronta Quote' },
  { path: '/impostazioni', icon: Settings, label: t.nav.settings },
];

// Sidebar uses relative paths for cross-domain compatibility

export default function Sidebar() {
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [highEvAlerts, setHighEvAlerts] = useState({ count: 0, alerts: [] });
  const [alertFlash, setAlertFlash] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [criticalAnomalies, setCriticalAnomalies] = useState(0);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`/api/alerts/high-ev-live`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.count > 0 && data.count !== highEvAlerts.count) {
          setAlertFlash(true);
          setTimeout(() => setAlertFlash(false), 5000);
          // Play alert sound
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            osc.type = 'sine';
            gain.gain.value = 0.08;
            osc.start();
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
            osc.stop(ctx.currentTime + 0.3);
          } catch (e) { /* audio not available */ }
        }
        setHighEvAlerts(data);
      }
    } catch (e) { /* silent fail for polling */ }
  }, [highEvAlerts.count]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`/api/notifications/unread-count`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnreadNotifCount(data.unread_count);
      }
    } catch (e) { /* silent */ }
  }, []);

  const fetchAnomalies = useCallback(async () => {
    try {
      const res = await fetch(`/api/tt/admin/ai-anomaly-history?limit=1`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const latest = data?.scans?.[0];
        if (latest?.anomalies) {
          const crit = latest.anomalies.filter(a => a.severity === 'critical').length;
          setCriticalAnomalies(crit);
        } else {
          setCriticalAnomalies(0);
        }
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchUnreadCount();
    fetchAnomalies();
    const interval = setInterval(() => {
      fetchAlerts();
      fetchUnreadCount();
      fetchAnomalies();
    }, 60000); // Poll every 60s instead of 15s
    return () => clearInterval(interval);
  }, [fetchAlerts, fetchUnreadCount, fetchAnomalies]);

  return (
    <aside
      className={`fixed left-0 top-0 h-full z-50 flex flex-col transition-all duration-300
        ${collapsed ? 'w-16' : 'w-60'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}
      style={{ background: '#09090B', borderRight: '1px solid #27272A' }}
    >
      {/* Logo + mobile close */}
      <div
        className="flex items-center justify-between h-16 px-4 flex-shrink-0"
        style={{ borderBottom: '1px solid #27272A' }}
      >
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-sm flex items-center justify-center">
            <Zap size={15} className="text-black fill-black" />
          </div>
          {!collapsed && (
            <span
              className="font-bold text-zinc-50 text-lg tracking-tight whitespace-nowrap"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              PointEdge
            </span>
          )}
        </div>
        <button
          data-testid="mobile-sidebar-close"
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1.5 rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800"
        >
          <X size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-hidden">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

          const isLiveNav = item.path === '/segnali-live';
          const showBadge = isLiveNav && highEvAlerts.count > 0;

          const isNotifNav = item.path === '/notifiche';
          const showNotifBadge = isNotifNav && unreadNotifCount > 0;

          const isAiNav = item.path === '/ai-advisor';
          const showAnomalyBadge = isAiNav && criticalAnomalies > 0;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace(/\//g, '') || 'dashboard'}`}
              title={collapsed ? item.label : undefined}
              className={`flex items-center h-9 my-0.5 mx-2 rounded-sm transition-colors duration-150 group relative
                ${isActive
                  ? 'bg-zinc-800/80 text-emerald-400 border-l-2 border-emerald-500'
                  : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200 border-l-2 border-transparent'}
                ${collapsed ? 'px-[11px]' : 'px-[13px]'}
                ${showBadge && alertFlash ? 'high-ev-flash' : ''}
              `}
            >
              <Icon size={17} className="flex-shrink-0" />
              {!collapsed && (
                <span className="ml-3 text-sm whitespace-nowrap" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {item.label}
                </span>
              )}
              {showBadge && (
                <span
                  data-testid="high-ev-alert-badge"
                  className={`ml-auto flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-bold transition-all ${
                    alertFlash ? 'bg-red-500 text-white animate-pulse' : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {collapsed ? highEvAlerts.count : `${highEvAlerts.count} EV`}
                </span>
              )}
              {showNotifBadge && (
                <span
                  data-testid="unread-notif-badge"
                  className="ml-auto flex-shrink-0 flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400"
                >
                  {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                </span>
              )}
              {showAnomalyBadge && (
                <span
                  data-testid="critical-anomaly-badge"
                  title={`${criticalAnomalies} anomalie critiche`}
                  className="ml-auto flex-shrink-0 flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400 animate-pulse"
                >
                  {collapsed ? criticalAnomalies : `${criticalAnomalies} ⚠`}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* High EV alert box */}
      {highEvAlerts.count > 0 && !collapsed && (
        <div
          data-testid="high-ev-alert-box"
          className={`mx-2 mb-2 rounded-sm p-3 cursor-pointer transition-all ${alertFlash ? 'ring-1 ring-red-500/50' : ''}`}
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
          onClick={() => navigate('/segnali-live')}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Volume2 size={12} className="text-red-400" />
            <span className="text-xs font-semibold text-red-400">Alert EV &gt; 20%</span>
          </div>
          {highEvAlerts.alerts.slice(0, 2).map((a) => (
            <div key={a.id} className="text-xs text-zinc-400 mt-1 truncate">
              <span className="text-red-400 font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                +{(a.expected_value * 100).toFixed(0)}%
              </span>{' '}
              {a.selection_name}
            </div>
          ))}
        </div>
      )}

      {/* Admin link */}
      <div className="mx-2 mb-2">
        <NavLink
          to="/admin"
          data-testid="nav-admin"
          title={collapsed ? 'Admin' : undefined}
          className={`flex items-center h-8 rounded-sm transition-colors text-zinc-600 hover:text-zinc-400 hover:bg-zinc-800/30 ${collapsed ? 'px-[11px]' : 'px-[13px]'}`}
        >
          <Shield size={14} className="flex-shrink-0" />
          {!collapsed && <span className="ml-3 text-xs whitespace-nowrap" style={{ fontFamily: 'Figtree, sans-serif' }}>Admin</span>}
        </NavLink>
      </div>

      {/* Collapse toggle (desktop) / Logout (mobile) */}
      <div className="p-3 flex-shrink-0 space-y-1" style={{ borderTop: '1px solid #27272A' }}>
        <button
          onClick={logout}
          data-testid="sidebar-logout"
          className={`md:hidden flex items-center w-full h-8 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-800/50 transition-colors ${
            collapsed ? 'justify-center' : 'px-3'
          }`}
        >
          <LogOut size={14} />
          {!collapsed && <span className="ml-3 text-xs">Logout</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
          className={`hidden md:flex items-center justify-center w-8 h-8 rounded-sm hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors ${
            collapsed ? 'mx-auto' : 'ml-auto'
          }`}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>
    </aside>
  );
}
